// Cross-device hydration — pure helper (task #20).
//
// Problem: the auth-gate ladder in App.jsx derives `needsOnboarding` from
// LOCAL state only — `!cloudStudent && !progress.onboardingComplete`, both
// rooted in the local children registry + localStorage progress. On a fresh
// device (or after a localStorage clear), local has nothing even when the
// cloud already holds this parent's students, so the app wrongly prompts
// re-onboarding. That breaks the "Progress syncs across all your devices"
// promise. (PR #62's lookup-before-create dedups the resulting students row,
// so no DUPLICATE is created — but the parent still sits through onboarding.)
//
// Fix: when the cloud has settled with students AND the local registry is
// empty for a signed-in user, recreate each cloud student as a local child
// PRE-LINKED via supabaseStudentId, with onboardingComplete=true. The gate
// ladder then sees a non-null cloudStudent + onboardingComplete progress and
// skips onboarding — the kid lands on PathHome ("Hi, Sasha!").
//
// This module is the PURE core: React-free so every branch is unit-tested
// without a renderer. The React effect in App.jsx maps each returned
// descriptor → addChildStore() (which generates the local uid) and
// saveProgressForChild(), then selects the active child by `activeIndex`.

import { defaultProgress } from "./progress.ts";

/**
 * Compute the local children to create from the user's cloud students.
 *
 * Guards (mirrored by the React effect's early-returns): a signed-in user,
 * at least one cloud student, and a GENUINELY EMPTY local registry. A
 * non-empty registry means this device already has the kids (or the parent
 * just added one) — hydrating would duplicate, so it's a no-op. This guard
 * is what makes the operation idempotent: once hydration has populated the
 * registry, a second call sees children > 0 and returns shouldHydrate=false.
 *
 * @param {Array<object>} cloudStudents - rows from fetchStudents(). Already
 *   ordered created_at ascending by the query; re-sorted here defensively.
 * @param {{id: string}|null|undefined} user - the signed-in user.
 * @param {{children: Array, activeChildId: string|null}} registry - the
 *   current local registry, filtered to this user.
 * @returns {{
 *   shouldHydrate: boolean,
 *   children: Array<{ addArgs: object, progress: object }>,
 *   activeIndex: number,
 * }} `children` ordered created_at ascending; `activeIndex` points at the
 *   child that should become active (the first/oldest). When shouldHydrate
 *   is false, children=[] and activeIndex=-1.
 */
export function hydrateLocalFromCloud(cloudStudents, user, registry) {
  const noop = { shouldHydrate: false, children: [], activeIndex: -1 };

  if (!user || !user.id) return noop;
  if (!Array.isArray(cloudStudents) || cloudStudents.length === 0) return noop;
  if (!registry || !Array.isArray(registry.children) || registry.children.length > 0) {
    return noop;
  }

  // Deterministic active-child selection across devices: oldest cloud row
  // (smallest created_at) wins. fetchStudents already orders ascending; the
  // defensive re-sort means a caller that hands us an unordered list still
  // gets a stable result. Rows without created_at sort to the front (0).
  const ordered = [...cloudStudents].sort((a, b) => {
    const ta = a && a.created_at ? Date.parse(a.created_at) : 0;
    const tb = b && b.created_at ? Date.parse(b.created_at) : 0;
    return ta - tb;
  });

  const children = ordered.map((cs) => {
    const base = defaultProgress();
    const age = cs.age ?? null;
    const progress = {
      ...base,
      onboardingComplete: true,
      studentName: cs.name,
      studentAge: age,
      // Fall back to the default rather than persist undefined/null for the
      // grade + module pointers. This mirrors the existing cloudStudent
      // hydration effect in App.jsx (`cloud.x || prev.x || default`) and the
      // createStudent column defaults. When math_level_id is absent the kid
      // still needs placement (placementStatus below stays "not_started"),
      // and PlacementCheckCard (Parent Settings) is the retake surface that
      // would overwrite the module pointer — so the fallback value is only
      // ever a transient placeholder.
      studentGrade: cs.grade || base.studentGrade,
      mathModuleId: cs.math_level_id || base.mathModuleId,
      readingModuleId: cs.reading_level_id || base.readingModuleId,
      // Track I.7: restore the parent ability-priors persisted on the cloud
      // students row so a fresh device doesn't lose them (and the engine seed
      // / abilities recap stay consistent across devices). Pre-0012 rows have
      // no column → undefined → fall back to the empty default.
      parentPriors: cs.parent_priors || base.parentPriors,
      // A cloud row WITH a math level means placement already happened on
      // another device — don't make the kid redo it. WITHOUT one, leave it
      // "not_started": the kid gets age-band default modules and no
      // automatic nudge (MA-155 removed the needsPlacement gate as a
      // confirmed dead end — same as a kid who explicitly skipped
      // placement always did); a parent can retake via PlacementCheckCard.
      placementStatus: cs.math_level_id ? "complete" : "not_started",
    };
    // Only attach a mascot when the cloud row actually has one. Writing
    // { id: null } would make the gate's `!progress.mascot` check think a pal
    // was already picked and wrongly skip MascotPick.
    if (cs.mascot_id) progress.mascot = { id: cs.mascot_id };

    return {
      addArgs: {
        name: cs.name,
        age,
        ownerUserId: user.id,
        // Pre-link at create time so the child resolves to the right cloud
        // row immediately — no reliance on the later auto-link-by-name pass.
        supabaseStudentId: cs.id,
        // MA-582 mislink-repair (D2/E1) — a plain provenance boolean: this
        // entry's local progress is a COPY of a cloud record, hydrated onto
        // this device rather than created here. Needs addChild's ...rest fix
        // (childrenStore.js) to actually land on the registry entry — before
        // that fix a fourth+ field silently vanished at the four-field
        // destructure. Its ONLY consumer is SettingsKids' Remove-confirm
        // data-loss warning (ParentSettingsSections.tsx): a tagged entry gets
        // the honest "this device's copy may be the only one we can still
        // vouch for" framing instead of the untagged cautious default. It
        // NEVER gates an action and never sharpens the unverified-link
        // warning — entries hydrated BEFORE this PR shipped are untagged
        // (this field did not exist), so the tag can only ever be a positive
        // signal about FUTURE hydrations, never a negative one about past
        // ones.
        hydratedFromCloud: true,
      },
      progress,
    };
  });

  return { shouldHydrate: true, children, activeIndex: 0 };
}
